import { Bell, BookOpen, Check, Download, LayoutDashboard, Loader2, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@Components/ui-v2/badge';
import { Button } from '@Components/ui-v2/button';

interface SplitLayoutProps {
  submitted: boolean;
  onSubmit: () => void;
  name?: string;
}

type StepStatus = 'done' | 'current' | 'pending';

interface StepDef {
  id: number;
  labelKey: string;
  detailKey: string;
  status: StepStatus;
}

const STEPS: StepDef[] = [
  {
    id: 1,
    labelKey: 'SupplierOnboarding.Steps.ConnectedLabel',
    detailKey: 'SupplierOnboarding.Steps.ConnectedDetail',
    status: 'done',
  },
  {
    id: 2,
    labelKey: 'SupplierOnboarding.Steps.ProcessingLabel',
    detailKey: 'SupplierOnboarding.Steps.ProcessingDetail',
    status: 'current',
  },
  {
    id: 3,
    labelKey: 'SupplierOnboarding.Steps.ReadyLabel',
    detailKey: 'SupplierOnboarding.Steps.ReadyDetail',
    status: 'pending',
  },
];

const FEATURES = [
  {
    Icon: LayoutDashboard,
    labelKey: 'SupplierOnboarding.Features.DashboardLabel',
    descriptionKey: 'SupplierOnboarding.Features.DashboardDescription',
  },
  {
    Icon: Bell,
    labelKey: 'SupplierOnboarding.Features.AlertsLabel',
    descriptionKey: 'SupplierOnboarding.Features.AlertsDescription',
  },
  {
    Icon: ShoppingCart,
    labelKey: 'SupplierOnboarding.Features.ChainSummaryLabel',
    descriptionKey: 'SupplierOnboarding.Features.ChainSummaryDescription',
  },
  {
    Icon: Download,
    labelKey: 'SupplierOnboarding.Features.ExportLabel',
    descriptionKey: 'SupplierOnboarding.Features.ExportDescription',
  },
];

const STEP_BADGE_CLASSES: Record<StepStatus, string> = {
  done: 'bg-success-50 border-success-500 text-success-600',
  current: 'bg-white border-brand-500 text-brand-700 animate-pulse',
  pending: 'bg-white border-gray-200 text-gray-400',
};

const STEP_LABEL_CLASSES: Record<StepStatus, string> = {
  done: 'text-success-700',
  current: 'text-foreground',
  pending: 'text-gray-400',
};

const SplitLayout = ({ submitted, onSubmit, name }: SplitLayoutProps) => {
  const { t } = useTranslation();
  const firstName = name?.split(' ')[0];

  return (
    <div className="flex-1 flex flex-col justify-center min-h-0 overflow-y-auto">
      {/* Top split: content + dashboard preview */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 px-8 lg:px-14 py-16 w-full max-w-7xl mx-auto">
        {/* Left — content */}
        <div className="flex flex-col gap-7 max-w-[520px]">
          <Badge variant="brand" className="w-fit">
            <Loader2 size={12} className="animate-spin" />
            {t('SupplierOnboarding.Badge')}
          </Badge>

          <div className="flex flex-col gap-3">
            <h1 className="text-[40px] font-bold tracking-tight leading-[1.1] text-foreground">
              {firstName
                ? t('SupplierOnboarding.WelcomeWithName', { name: firstName })
                : t('SupplierOnboarding.WelcomeGeneric')}
            </h1>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('SupplierOnboarding.Description')}{' '}
              <strong className="text-foreground font-semibold">{t('SupplierOnboarding.DescriptionTimeframe')}</strong>.
            </p>
          </div>

          {/* Vertical step progress — connector is absolute so it spans badge bottom → next badge top */}
          <ol className="flex flex-col">
            {STEPS.map((step, i) => (
              <li key={step.id} className="relative flex gap-3.5 pb-5 last:pb-0">
                {/* Connector line (absolute, anchored to li) */}
                {i < STEPS.length - 1 && (
                  <div
                    className={`absolute left-[13px] top-[30px] bottom-0 w-0.5 ${
                      step.status === 'done' ? 'bg-success-300' : 'bg-gray-200'
                    }`}
                  />
                )}
                {/* Badge */}
                <div
                  className={`size-7 rounded-full inline-flex items-center justify-center text-[12px] font-semibold border-2 shrink-0 relative ${
                    STEP_BADGE_CLASSES[step.status]
                  }`}>
                  {step.status === 'current' && (
                    <span className="absolute -inset-1 rounded-full bg-brand-500/20 animate-ping [animation-duration:2s]" />
                  )}
                  <span className="relative">
                    {step.status === 'done' ? <Check size={14} strokeWidth={3} /> : step.id}
                  </span>
                </div>
                {/* Label + detail */}
                <div className="flex-1 pt-0.5">
                  <div className={`text-sm font-semibold ${STEP_LABEL_CLASSES[step.status]}`}>{t(step.labelKey)}</div>
                  <div className={`text-xs mt-0.5 ${step.status === 'pending' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t(step.detailKey)}
                  </div>
                </div>
              </li>
            ))}
          </ol>

          {/* Actions */}
          {submitted ? (
            <div className="inline-flex w-fit items-center gap-2.5 px-4 py-2.5 rounded-full bg-success-50 border border-success-200 text-success-700 text-sm font-medium">
              <Check size={16} strokeWidth={2.5} />
              {t('SupplierOnboarding.EmailWhenReady')}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={onSubmit} className="gap-2">
                <Bell className="size-4" />
                {t('SupplierOnboarding.NotifyMe')}
              </Button>
              <Button variant="brand-secondary" className="gap-2">
                <BookOpen className="size-4" />
                {t('SupplierOnboarding.ViewTraining')}
              </Button>
            </div>
          )}
        </div>

        {/* Right — skeleton dashboard preview (single animate-pulse wrapper = synced breathing) */}
        <div className="flex flex-col gap-2.5 lg:pt-2">
          <div className="rounded-2xl border bg-white p-5 flex flex-col gap-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
            <div className="animate-pulse flex flex-col gap-5">
              {/* KPI tiles row */}
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="bg-gray-100 rounded-xl p-3 h-[52px] flex items-center">
                    <div className="h-2 w-16 bg-gray-200 rounded-md" />
                  </div>
                ))}
              </div>

              {/* Charts row: donut + line chart */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-100 rounded-xl p-4 flex items-center justify-center min-h-[120px]">
                  <div className="size-20 rounded-full border-[8px] border-gray-200" />
                </div>
                <div className="bg-gray-100 rounded-xl p-4 min-h-[120px] flex items-center">
                  <svg viewBox="0 0 200 60" className="w-full h-12" preserveAspectRatio="none">
                    <path
                      d="M 0 50 L 30 35 L 60 40 L 90 20 L 120 25 L 150 10 L 180 15 L 200 5"
                      stroke="#d4d4d8"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>

              {/* Table rows */}
              <div className="bg-gray-100 rounded-xl p-3 flex flex-col gap-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <div className="h-2 w-1/4 bg-gray-200 rounded-md" />
                    <div className="h-2 w-1/3 bg-gray-200 rounded-md" />
                    <div className="h-2 w-1/5 bg-gray-200 rounded-md" />
                    <div className="h-2 w-1/6 bg-gray-200 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500">{t('SupplierOnboarding.PreviewLabel')}</p>
        </div>
      </div>

      {/* Bottom: features grid */}
      <div className="border-t px-8 lg:px-14 py-10 w-full">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-gray-500 mb-5">
            {t('SupplierOnboarding.PanelIncludes')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((feature) => (
              <div
                key={feature.labelKey}
                className="bg-white border rounded-2xl p-5 flex flex-col gap-3 transition-shadow hover:shadow-[0_4px_12px_rgba(16,24,40,0.04)]">
                <div className="size-10 rounded-xl bg-brand-50 flex items-center justify-center">
                  <feature.Icon className="size-5 text-brand-700" strokeWidth={2} />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-foreground">{t(feature.labelKey)}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{t(feature.descriptionKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitLayout;
